import { type Editor, type Extensions } from "@tiptap/core";
import { type ComponentType, memo, useCallback, useEffect, useMemo, useRef } from "react";

import { useTracking } from "utilities/analytics/analytics";

import BTEditorChatInterface from "commonComponents/btWrappers/editor/BTEditorChat/BTEditorChatInterface";
import {
    addBreak,
    addNewParagraph,
    getReactEditorStateFromTiptapEditor,
    useFrequentEditorRemountSafeguard,
} from "commonComponents/btWrappers/editor/core/editor.utils";
import ExtensionProvider from "commonComponents/btWrappers/editor/core/extensions/ExtensionProvider";
import { type IChatWithImageFeatureSetConfig } from "commonComponents/btWrappers/editor/core/extensions/ExtensionProvider.types";
import { type IBTEditorBaseProps } from "commonComponents/btWrappers/editor/editor.types";
import { type DiscussionCommentEntity } from "commonComponents/entity/comment/CommentContainer/CommentContainer.api.types";
import { withErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";

interface IBTEditorChatProps<FormValues, ActionType extends string | undefined = string>
    extends IBTEditorBaseProps<FormValues> {
    /**
     * **Should be memoized.**
     *
     *
     * Optional configurations and any additional event handler bindings to the many editor
     * controls/extensions.
     *
     * @example
     * // Overrides document schema to force bold text
     * const myConfig = React.useMemo(() => ({
     *      document: {
     *          extend: {
     *              content: "paragraph",
     *          },
     *      },
     *      paragraph: {
     *          extend: {
     *              renderHTML({ HTMLAttributes }) {
     *                  return ["p", HTMLAttributes, ["strong", {}, 0]];
     *              },
     *          },
     *      },
     *    },
     * }), []);
     * <BTEditorChat
     *     extensionConfig={myConfig}
     * />
     */
    extensionConfig?: Omit<IChatWithImageFeatureSetConfig, "disableNewParagraph">;
    onSendChat?: () => void;
    actionBeingPerformed?: ActionType;
    loadingAction?: ActionType;
    isWebView?: boolean;
    /**
     * As of right now, BTEditorChat is used for entity comments and Chat. Eventually, it would be
     * nice if both supported images, but for now, we will need to implement images for Chat first
     * @default false
     **/
    disableImages?: boolean;
    /**
     * If true, disables submitting chat on enter key press.
     * @default false
     */
    disableSubmitOnEnterKey?: boolean;
    style?: React.CSSProperties;
    onClearReplyContent?: () => void;
    addReplyToListener?: (listener: (comment: DiscussionCommentEntity) => void) => void;
    replyContentClassName?: string;
}

/**
 * WARNING: Be careful using this component as opposed to BTEditor/BTEditorBasic. This component does not lazily load TipTap bundles,
 * so only use this component in a component that is also lazily loaded.
 */
function BTEditorChatInternal<
    FormValues = undefined,
    ActionType extends string | undefined = string,
>({
    extensionConfig,
    value,
    onSendChat,
    actionBeingPerformed,
    isWebView,
    disableImages = false,
    disableSubmitOnEnterKey = false,
    ...otherProps
}: IBTEditorChatProps<FormValues, ActionType>) {
    useFrequentEditorRemountSafeguard(value);

    const { trackEvent } = useTracking();

    const handleEnterPressed = useCallback(
        (editor: Editor) => {
            if (actionBeingPerformed) return;
            if (disableSubmitOnEnterKey || isWebView) {
                addBreak(editor);
            } else {
                if (getReactEditorStateFromTiptapEditor(editor) === "inlineControlsOnly") {
                    trackEvent({
                        event: "Hotkey",
                        uniqueId: "sendChat",
                    });
                    onSendChat?.();
                } else {
                    addBreak(editor);
                }
            }
        },
        [actionBeingPerformed, isWebView, onSendChat, trackEvent, disableSubmitOnEnterKey],
    );

    const handleShiftEnterPressed = useCallback(
        (editor: Editor) => {
            if (actionBeingPerformed) return;
            if (disableSubmitOnEnterKey || isWebView) {
                addNewParagraph(editor);
            } else {
                if (getReactEditorStateFromTiptapEditor(editor) === "inlineControlsOnly") {
                    addNewParagraph(editor);
                } else {
                    addBreak(editor);
                }
            }
        },
        [actionBeingPerformed, isWebView, disableSubmitOnEnterKey],
    );

    /**
     * Editor extensions are defined and configured statically on editor mount. Therefore, we need a static
     * function bound to this component's lifetime to trigger the onSendChat handler.
     *
     * We create a ref here which allows us to store this lifetime-bound function, and update the ref
     * anytime the parent event chants. Then we configure the overrideEnterKey extension to fire
     * the current ref's function on enter.
     */
    const handleEnterPressedStaticRef = useRef(handleEnterPressed);
    useEffect(() => {
        handleEnterPressedStaticRef.current = handleEnterPressed;
    }, [handleEnterPressed]);
    const handleShiftEnterPressedStaticRef = useRef(handleShiftEnterPressed);
    useEffect(() => {
        handleShiftEnterPressedStaticRef.current = handleShiftEnterPressed;
    }, [handleShiftEnterPressed]);

    const configWithEnterHandler = useMemo<IChatWithImageFeatureSetConfig>(
        () => ({
            ...extensionConfig,
            overrideTopLevelEnterKey: {
                configure: {
                    onEnterStaticHandler: (editor: Editor) =>
                        handleEnterPressedStaticRef.current(editor),
                    onShiftEnterStaticHandler: (editor: Editor) =>
                        handleShiftEnterPressedStaticRef.current(editor),
                },
            },
        }),
        [extensionConfig],
    );
    return (
        <ExtensionProvider
            featureSet={disableImages ? "chat" : "chatWithImages"}
            extensionConfig={configWithEnterHandler}
        >
            {(extensions: Extensions) => (
                // eslint-disable-next-line react/forbid-elements
                <BTEditorChatInterface
                    extensions={extensions}
                    value={value}
                    onSendChat={onSendChat}
                    actionBeingPerformed={actionBeingPerformed}
                    {...otherProps}
                />
            )}
        </ExtensionProvider>
    );
}

const BTEditorChatWithErrorBoundary = withErrorBoundary(BTEditorChatInternal as ComponentType)(
    "There was an error loading the rich text editor. Please refresh the page or contact support if this issue persists.",
) as typeof BTEditorChatInternal;
export const BTEditorChat = memo(BTEditorChatWithErrorBoundary) as typeof BTEditorChatInternal;
